import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectV2, setFocusField } from './v2Upload.slice';

/** Runs at the wizard level. When a jump sets `focusField`, this scrolls the
 * matching `[data-focus="…"]` element into view, rings it briefly, and focuses
 * the first control inside — then clears the flag so it fires exactly once.
 * Outline (not a layout box) is used for the ring so it's never clipped by an
 * overflow container. */
export default function useFocusField(): void {
  const dispatch = useDispatch();
  const { focusField } = useSelector(selectV2);

  useEffect(() => {
    if (!focusField) return undefined;

    const el = document.querySelector<HTMLElement>(
      `[data-focus="${focusField}"]`
    );
    dispatch(setFocusField(null));
    if (!el) return undefined;

    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    el
      .querySelector<HTMLElement>('input, textarea, button, [tabindex]')
      ?.focus({
        preventScroll: true,
      });

    // An invalid field already wears red on its own control; a second ring
    // around the whole block would enclose the label and the message too.
    if (el.dataset.invalid) return undefined;

    el.style.outline = '2px solid hsl(var(--primary))';
    el.style.outlineOffset = '4px';
    el.style.borderRadius = '12px';

    const timer = setTimeout(() => {
      el.style.outline = '';
      el.style.outlineOffset = '';
      el.style.borderRadius = '';
    }, 1800);
    return () => clearTimeout(timer);
  }, [focusField, dispatch]);
}
